import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval } from "date-fns";
import { tr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, User, Clock } from "lucide-react";

export default function AdminCalendar() {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState("");
  const [view, setView] = useState("day"); // day, week
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBarbers = async () => {
      try {
        const response = await axios.get(`${API}/barbers`);
        setBarbers(response.data);
      } catch (error) {
        console.error("Failed to fetch barbers");
      }
    };
    fetchBarbers();
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [token, selectedDate, selectedBarber, view]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      let dates = [];
      if (view === "day") {
        dates = [format(selectedDate, "yyyy-MM-dd")];
      } else {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
        dates = eachDayOfInterval({ start: weekStart, end: weekEnd }).map(d => format(d, "yyyy-MM-dd"));
      }
      
      const allAppointments = [];
      for (const date of dates) {
        const params = new URLSearchParams({ date });
        if (selectedBarber) params.append("barber_id", selectedBarber);
        
        const response = await axios.get(`${API}/appointments?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allAppointments.push(...response.data);
      }
      
      setAppointments(allAppointments);
    } catch (error) {
      toast.error("Randevular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (direction) => {
    const days = view === "week" ? 7 : 1;
    setSelectedDate(prev => addDays(prev, direction * days));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-amber-500",
      approved: "bg-emerald-600",
      completed: "bg-blue-600",
      rejected: "bg-red-600",
      cancelled: "bg-[#9ca3af]"
    };
    return colors[status] || "bg-[#9ca3af]";
  };

  const timeSlots = [];
  for (let hour = 9; hour <= 19; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, "0")}:00`);
    timeSlots.push(`${hour.toString().padStart(2, "0")}:30`);
  }

  const getAppointmentsForSlot = (date, time) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return appointments.filter(a => a.date === dateStr && a.time === time);
  };

  const weekDays = view === "week" 
    ? eachDayOfInterval({
        start: startOfWeek(selectedDate, { weekStartsOn: 1 }),
        end: endOfWeek(selectedDate, { weekStartsOn: 1 })
      })
    : [selectedDate];

  return (
    <AdminLayout title="Takvim">
      <div data-testid="admin-calendar">
        {/* Controls */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigateDate(-1)} variant="ghost" className="text-[#6b7280]">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <h2 className="font-display text-xl text-[#1a1a2e] min-w-[200px] text-center">
              {view === "week" 
                ? `${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "d MMM", { locale: tr })} - ${format(endOfWeek(selectedDate, { weekStartsOn: 1 }), "d MMM yyyy", { locale: tr })}`
                : format(selectedDate, "d MMMM yyyy", { locale: tr })
              }
            </h2>
            
            <Button onClick={() => navigateDate(1)} variant="ghost" className="text-[#6b7280]">
              <ChevronRight className="w-5 h-5" />
            </Button>
            
            <Button 
              onClick={() => setSelectedDate(new Date())} 
              variant="ghost" 
              className="text-[#c9a96e]"
            >
              Bugün
            </Button>
          </div>
          
          <div className="flex gap-4">
            <Select value={selectedBarber || "all"} onValueChange={(v) => setSelectedBarber(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] bg-[#fafafa] border-[#e5e7eb] text-[#1a1a2e]">
                <SelectValue placeholder="Tüm Berberler" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#e5e7eb]">
                <SelectItem value="all">Tüm Berberler</SelectItem>
                {barbers.map((barber) => (
                  <SelectItem key={barber.id} value={barber.id}>{barber.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex bg-white border border-[#e5e7eb] rounded-lg overflow-hidden">
              <button
                onClick={() => setView("day")}
                className={`px-4 py-2 text-sm font-accent ${view === "day" ? "bg-[#c9a96e] text-white" : "text-[#6b7280]"}`}
              >
                Gün
              </button>
              <button
                onClick={() => setView("week")}
                className={`px-4 py-2 text-sm font-accent ${view === "week" ? "bg-[#c9a96e] text-white" : "text-[#6b7280]"}`}
              >
                Hafta
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-x-auto shadow-sm">
            <div className="min-w-[800px]">
              {/* Header */}
              <div className="grid border-b border-[#e5e7eb]" style={{ gridTemplateColumns: `80px repeat(${weekDays.length}, 1fr)` }}>
                <div className="p-4 border-r border-[#e5e7eb]"></div>
                {weekDays.map((day) => (
                  <div 
                    key={day.toISOString()} 
                    className="p-4 text-center border-r border-[#e5e7eb] last:border-r-0"
                  >
                    <p className="font-accent text-xs text-[#6b7280]">
                      {format(day, "EEEE", { locale: tr })}
                    </p>
                    <p className={`font-display text-lg ${format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd") ? "text-[#c9a96e]" : "text-[#1a1a2e]"}`}>
                      {format(day, "d")}
                    </p>
                  </div>
                ))}
              </div>

              {/* Time Slots */}
              <div className="max-h-[600px] overflow-y-auto">
                {timeSlots.map((time) => (
                  <div 
                    key={time} 
                    className="grid border-b border-[#f3f4f6]"
                    style={{ gridTemplateColumns: `80px repeat(${weekDays.length}, 1fr)` }}
                  >
                    <div className="p-2 text-[#9ca3af] text-sm text-right pr-4 border-r border-[#e5e7eb]">
                      {time}
                    </div>
                    {weekDays.map((day) => {
                      const dayAppointments = getAppointmentsForSlot(day, time);
                      return (
                        <div 
                          key={`${day.toISOString()}-${time}`}
                          className="p-1 min-h-[60px] border-r border-[#f3f4f6] last:border-r-0"
                        >
                          {dayAppointments.map((appt) => (
                            <div 
                              key={appt.id}
                              className={`p-2 mb-1 rounded ${getStatusColor(appt.status)} text-white text-xs`}
                              title={`${appt.customer_name || appt.customer_phone} - ${appt.service_name}`}
                            >
                              <div className="flex items-center gap-1 mb-1">
                                <User className="w-3 h-3" />
                                <span className="truncate">{appt.customer_name || appt.customer_phone}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>{appt.service_name}</span>
                              </div>
                              {!selectedBarber && (
                                <div className="mt-1 opacity-75">{appt.barber_name}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-500"></div>
            <span className="text-[#6b7280] text-sm">Bekliyor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-600"></div>
            <span className="text-[#6b7280] text-sm">Onaylandı</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-600"></div>
            <span className="text-[#6b7280] text-sm">Tamamlandı</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-600"></div>
            <span className="text-[#6b7280] text-sm">Reddedildi</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#9ca3af]"></div>
            <span className="text-[#6b7280] text-sm">İptal</span>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
